import {
  AgBaseChartThemeOptions,
  AgChartLabelFormatterParams,
  AgChartOptions,
  AgDonutInnerLabel,
} from "ag-charts-community";
import { ChartSeriesTypeEnum } from "gen/model/chartSeriesTypeEnum";
import { GenericChartData } from "gen/model/genericChartData";
import { MaybernAgChartTheme } from "shared/components/chart/MaybernAgChartTheme";
import { MaybernChart } from "shared/components/chart/MaybernChart";
import {
  chartDataConverter,
  tooltipRenderer,
} from "shared/components/chart/utils";
import colors from "theme/colors";

type AxisConfig = {
  label?: string;
  labelFormatter?: (params: { value: string }) => string;
  hideTicks?: boolean;
};

type SeriesConfig<T> = {
  tooltipKey?: string;
  innerLabels?: AgDonutInnerLabel[];
  stacked?: boolean;
  direction?: string;
  cornerRadius?: number;
  labelGenerator?: (key?: string | null) => {
    formatter: (params: AgChartLabelFormatterParams<T>) => string;
    color?: string;
  };
};

type MaybernChartWrapperProps<T> = GenericChartData & {
  palette?: AgBaseChartThemeOptions["palette"];
  xAxis?: AxisConfig;
  yAxis?: AxisConfig;
  hideLegend?: boolean;
  height?: number;
  padding?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  legendPosition?: "top" | "right" | "bottom" | "left";
  seriesConfig?: SeriesConfig<T>;
};

export const MaybernChartWrapper = <T,>(props: MaybernChartWrapperProps<T>) => (
  <MaybernChart
    options={
      {
        theme: {
          ...MaybernAgChartTheme,
          palette: props.palette || {
            strokes: [colors.cyan[300], colors.cyan[500]],
            fills: [colors.cyan[300], colors.cyan[500]],
          },
        },
        height: props.height,
        padding: props.padding || {
          top: 4,
          right: 0,
          bottom: 0,
          left: 0,
        },
        legend: {
          position: props.legendPosition ?? "right",
          enabled: !props.hideLegend,
        },
        data: props.data.map((datum) => chartDataConverter(datum)),
        series: props.series.map((series) => {
          switch (series.type) {
            case ChartSeriesTypeEnum.bar:
              return {
                type: series.type,
                xKey: series.x_key,
                yKey: series.y_key,
                yName: series.y_name,
                tooltip: {
                  renderer: tooltipRenderer(
                    props.seriesConfig?.tooltipKey ?? series.y_key,
                  ),
                },
                direction: props.seriesConfig?.direction,
                stacked: props.seriesConfig?.stacked,
                cornerRadius: props.seriesConfig?.cornerRadius,
                label: props.seriesConfig?.labelGenerator?.(series.y_key),
                stackGroup: series.stack_group,
              };
            case ChartSeriesTypeEnum.line:
              return {
                type: series.type,
                xKey: series.x_key,
                yKey: series.y_key,
                yName: series.y_name,
                tooltip: {
                  renderer: tooltipRenderer(series.y_key),
                },
              };
            case ChartSeriesTypeEnum.donut:
              return {
                type: series.type,
                tooltip: {
                  renderer: tooltipRenderer(props.seriesConfig?.tooltipKey),
                },
                angleKey: series.angle,
                innerRadiusRatio: 0.75,
                innerLabels: props.seriesConfig?.innerLabels,
              };
          }
        }),
        axes:
          props.x_axis && props.y_axis
            ? [
                {
                  type: props.x_axis.type,
                  position: "bottom",
                  title: {
                    text: props.x_axis.title,
                  },
                  nice: false,
                  line: {
                    enabled: false,
                  },
                  label: {
                    enabled:
                      (props.xAxis?.labelFormatter ||
                        (props.xAxis?.label && !props.xAxis.hideTicks)) ??
                      false,
                    formatter: props.xAxis?.labelFormatter,
                    format: props.xAxis?.label,
                  },
                  gridLine: {
                    enabled: false,
                  },
                  crosshair: {
                    enabled: false,
                  },
                  tick: {
                    enabled: !props.xAxis?.hideTicks,
                  },
                },
                {
                  type: props.y_axis.type,
                  position: "left",
                  nice: true,
                  title: props.y_axis.title
                    ? {
                        text: props.y_axis.title,
                      }
                    : undefined,
                  label: {
                    enabled:
                      (props.yAxis?.labelFormatter ||
                        (props.yAxis?.label && !props.yAxis.hideTicks)) ??
                      false,
                    formatter: props.yAxis?.labelFormatter,
                    format: props.yAxis?.label,
                  },
                  crosshair: {
                    enabled: false,
                  },
                  tick: {
                    enabled: !props.yAxis?.hideTicks,
                  },
                  line: {
                    enabled: false,
                  },
                },
              ]
            : [],
      } as AgChartOptions
    }
  />
);
